import { useEffect, useState } from "react";
import axios from "axios";
import SelectDocuments from "./SelectDocuments";
import SelectDocumentsReKYC from "../opportunity-documents/SelectDocumentsReKYC.jsx";
import { useLeadJourneyAddInfo } from "@/api/lead/useLeadJourneyAddInfo.jsx";
import { apiRequest } from "@/network/apiClient";
import { API_ENDPOINTS } from "@/api/apiEndpoints";
import { toast } from "react-toastify";
import ConfirmEmail from "./ConfirmEmail.jsx";
import ConfirmEmailReKYC from "../opportunity-documents/ConfirmEmailReKYC.jsx";

const AdditionalDetails = ({leadOwner, leadID, fromOpp, canViewLead}) => {

    const {companyRegNo, companyWebsite, addRemarksComp, addRemarksFarmer, addPurposeOfMM, accID, refetch} = useLeadJourneyAddInfo({leadID})

    const [formData, setFormData] = useState({
        companyWebsiteLink: "",
        companyRegistrationNumber: "",
        additionalRemarksCompliance: "",
        additionalRemarksFarmer: "",
        mmAccountID: "",
        purposeOfMM:"",
        leadID: leadID
    });

    const [showConfirmationModal, setShowConfirmationModal] = useState(false);
    const [showReassignConfirmationModal, setShowReassignConfirmationModal] = useState(false);
    const [showSendEmailConfirmationModal, setShowSendEmailConfirmationModal] = useState(false);

    const [isEditing, setIsEditing] = useState(false);
    const handleEditClick = () => {
        setIsEditing(true);
    };
    const handleSubmit = () => {
        setShowReassignConfirmationModal(true);
    };
    const handleSubmitEmail = () =>{
        setShowSendEmailConfirmationModal(true);
    };
    const handleCancel = () =>{
        setIsEditing(false); // exit edit mode
    };
    const handleConfirmationCancel = () => {
        setShowConfirmationModal(false); // Close the confirmation modal
        setShowReassignConfirmationModal(false);
    };
    const handleSendEmailConfirmationCancel = () => {
        setShowSendEmailConfirmationModal(false); // Close the confirmation modal
    };
    
    const handleChange = (e) => {
        const {name, value} = e.target;
        setFormData({
            ...formData,
            [name]: value
        });
    };

    const handleReassignConfirmation = async (e) => {
        e.preventDefault();
    
        try {
            // Upload the data to backend
            await apiRequest(
                `${import.meta.env.VITE_BASE_URL}/${API_ENDPOINTS.UPDATE_LEADS.ADDITIONAL_INFO}`,
                "POST",
                formData
            );
    
            // Backend succeeded for all fields (including mm_acc_id)
            refetch(); // sync latest values (purpose, website, reg no, mm_acc_id, etc.)
            toast.success("Additional Details successfully updated");
    
            // Only exit edit mode on full success
            setIsEditing(false);
        } catch (err) {
            console.error("Error updating additional information:", err);
    
            const message =
                err?.response?.data?.message ||
                err?.message ||
                "Error updating additional information";
    
            toast.error(message);
    
            // Backend still updated other fields (remarks, reg no, website),
            // so refetch to reflect those changes in the UI
            refetch();
    
            // If it's the duplicate MM Account ID error, clear ONLY that field
            if (message.includes("MM Account ID")) {
                setFormData((prev) => ({
                    ...prev,
                    mmAccountID: "",    // clear the invalid value
                }));
                // NOTE: we do NOT call setIsEditing(false) here
                // user stays in edit mode to fix mm_acc_id
            }
        } finally {
            // Close the confirmation modal in all cases
            setShowReassignConfirmationModal(false);
        }
    };    

    const areRequiredFieldsFilled = () => {
        // Only  check data when in edit mode
        if (isEditing) {
            return Boolean(
                (formData.mmAccountID || accID) && 
                (formData.companyRegistrationNumber || companyRegNo) && 
                (formData.companyWebsiteLink || companyWebsite) && 
                (formData.purposeOfMM || addPurposeOfMM)
            );
        }
        // Always allow entering edit mode
        return true
    };
    // Check required fill or not outside isEditing
    const filledRequiredFields = () => {
        return Boolean(
            (formData.mmAccountID || accID) && 
            (formData.companyRegistrationNumber || companyRegNo) && 
            (formData.companyWebsiteLink || companyWebsite) && 
            (formData.purposeOfMM || addPurposeOfMM)
        )
    }

    // Add function for sending email to compliance here
    const handleSendEmail = () => {
        setShowSendEmailConfirmationModal(false);
    };

    return (
        <>
        <div className= "mx-4 p-4 rounded-lg bg-white shadow-md">
            <div className="flex flex-col p-4 gap-4">
                <div className="flex justify-between items-start">
                    <h1 className="font-bold text-xl">Additional Details</h1>
                    {leadOwner || canViewLead ? (
                        <div className="flex gap-4 justify-end">
                            {isEditing && (
                                <button
                                    className="group bg-white text-black p-3 rounded-full border-[1px] border-black hover:bg-gray-200 transition flex items-center justify-center w-12 h-12"
                                    onClick={handleCancel}
                                    aria-label="Cancel Editing"
                                >
                                    <svg
                                        xmlns="http://www.w3.org/2000/svg"
                                        fill="none"
                                        viewBox="0 0 24 24"
                                        strokeWidth="1.5"
                                        stroke="currentColor"
                                        className="size-6"
                                    >
                                        <path
                                            strokeLinecap="round"
                                            strokeLinejoin="round"
                                            d="M6 18 18 6M6 6l12 12"
                                        />
                                    </svg>
                                </button>
                            )}
                            <button
                                className="group bg-white text-black p-3 rounded-full border-[1px] border-black hover:bg-yellow-300 transition flex items-center justify-center w-12 h-12"
                                onClick={isEditing ? (areRequiredFieldsFilled() ? handleSubmit : null ) : handleEditClick}
                                disabled={isEditing && !areRequiredFieldsFilled()}
                                aria-label={isEditing ? 'Submit' : 'Edit Lead'}
                                >
                                {isEditing ? (
                                    <svg
                                    xmlns="http://www.w3.org/2000/svg"
                                    fill="none"
                                    viewBox="0 0 24 24"
                                    strokeWidth="1.5"
                                    stroke="currentColor"
                                    className="size-6"
                                    >
                                        <path
                                            strokeLinecap="round"
                                            strokeLinejoin="round"
                                            d="m4.5 12.75 6 6 9-13.5"
                                        />
                                    </svg>
                                ) : (
                                    <svg
                                    xmlns="http://www.w3.org/2000/svg"
                                    fill="none"
                                    viewBox="0 0 24 24"
                                    strokeWidth="1.5"
                                    stroke="currentColor"
                                    className="size-6"
                                    >
                                        <path
                                            strokeLinecap="round"
                                            strokeLinejoin="round"
                                            d="m16.862 4.487 1.687-1.688a1.875 1.875 0 1 1 2.652 2.652L6.832 19.82a4.5 4.5 0 0 1-1.897 1.13l-2.685.8.8-2.685a4.5 4.5 0 0 1 1.13-1.897L16.863 4.487Zm0 0L19.5 7.125"
                                        />
                                    </svg>
                                )}
                            </button>
                        </div>
                        ): null}
                    </div>
                <form>
                    <h2 className="font-bold text-l underline mb-4">Remark for Compliance</h2>
                    <div className="grid grid-cols-1 gap-4 mt-2">
                        <div className="flex items-start mt-5">
                            <p className='border-b border-gray-200 pb-2 w-full flex'>
                                <span className="w-[18%] font-bold">Purpose of using MoneyMatch <a className="text-[#DD3730]">*</a></span>
                                {isEditing ? (
                                    <input
                                    type="text"
                                    name="purposeOfMM"
                                    value={formData.purposeOfMM}
                                    placeholder={addPurposeOfMM}
                                    onChange={handleChange}
                                    required
                                    className="flex-1 bg-gray-50 px-2 py-1 rounded border border-gray-200 text-left"
                                    />
                                ):(
                                    <span className="flex-1 flex items-center">
                                        <a>{addPurposeOfMM}</a>
                                    </span>
                                )}
                            </p>
                        </div>
                    </div>
                    <div className="grid grid-cols-2 gap-4">
                        <div className="flex items-start mt-5">
                            <p className='border-b border-gray-200 pb-2 w-full flex'>
                                <span className="w-1/3 font-bold">Company Website Link <a className="text-[#DD3730]">*</a></span>
                                {isEditing ? (
                                    <input
                                    type="text"
                                    name="companyWebsiteLink"
                                    value={formData.companyWebsiteLink}
                                    placeholder={companyWebsite}
                                    onChange={handleChange}
                                    required
                                    className="flex-1 bg-gray-50 px-2 py-2 rounded border border-gray-200 text-left"
                                    />
                                ) : (
                                    <span className="px-6 flex-1 flex items-center">
                                        <a>{companyWebsite}</a>
                                    </span>
                                )}
                            </p>
                        </div>
                        <div className="flex items-start mt-5">
                            <p className='border-b border-gray-200 pb-2 w-full flex'>
                                <span className="w-1/3 font-bold">Company Registration Number <a className="text-[#DD3730]">*</a></span>
                                {isEditing ? (
                                    <input
                                    type="text"
                                    name="companyRegistrationNumber"
                                    value={formData.companyRegistrationNumber}
                                    placeholder={companyRegNo}
                                    onChange={handleChange}
                                    required
                                    className="flex-1 bg-gray-50 px-2 py-1 rounded border border-gray-200 text-left"
                                    />
                                ):(
                                    <span className="px-6 flex-1 flex items-center">
                                        <a>{companyRegNo}</a>
                                    </span>
                                )}
                            </p>
                        </div>
                    </div>
                    <div className="grid grid-cols-1 gap-4 mt-4">
                        <div className="flex items-start mt-5">
                            <p className='border-b border-gray-200 pb-2 w-full flex'>
                                <span className="w-[18%] font-bold">Additional Remarks for Compliance</span>
                                {isEditing ? (
                                    <>
                                    <input
                                        type="text"
                                        name="additionalRemarksCompliance"
                                        value={formData.additionalRemarksCompliance}
                                        onChange={handleChange}
                                        placeholder={addRemarksComp}
                                        className="flex-1 bg-gray-50 px-2 py-1 rounded border border-gray-200 text-left"
                                        />
                                    </>
                                ):(
                                    <span className="flex-1 flex items-center">
                                        <a>{addRemarksComp}</a>
                                    </span>
                                )}
                            </p>
                        </div>
                    </div>
                    {/* Remark for farmers */}
                    <h2 className="font-bold text-l underline my-4 pt-4">Remark for Farmers</h2>
                    <div className="grid grid-cols-1 gap-4 mt-2">
                        <div className="flex items-start mt-5">
                            <p className='border-b border-gray-200 pb-2 w-full flex'>
                                <span className="w-[18%] font-bold">Additional Remarks for Farmers</span>
                                {isEditing ? (
                                    <>
                                        <input
                                        type="text"
                                        name="additionalRemarksFarmer"
                                        value={formData.additionalRemarksFarmer}
                                        placeholder={addRemarksFarmer}
                                        onChange={handleChange}
                                        className="flex-1 bg-gray-50 px-2 py-1 rounded border border-gray-200 text-left"
                                        />
                                    </>
                                ):(
                                    <span className="flex-1 flex items-center">
                                        <a>{addRemarksFarmer}</a>
                                    </span>
                                )}
                            </p>
                        </div>
                    </div>
                    <h2 className="font-bold text-l underline my-4 pt-4">MM Account ID Details</h2>
                    <div className="grid grid-cols-1 gap-4 mt-2">
                        <div className="flex items-start mt-5">
                            <p className='border-b border-gray-200 pb-2 w-full flex'>
                                <span className="w-[18%] font-bold">MM Account ID <a className="text-[#DD3730]">*</a></span>
                                {isEditing ? (
                                    <input
                                    type="text"
                                    name="mmAccountID"
                                    value={formData.mmAccountID}
                                    placeholder={accID}
                                    onChange={handleChange}
                                    required
                                    className="flex-1 bg-gray-50 px-2 py-1 rounded border border-gray-200 text-left"
                                    />
                                ):(
                                    <span className="flex-1 flex items-center">
                                        <a>{accID}</a>
                                    </span>
                                )}
                            </p>
                        </div>
                    </div>
                    <div className="mt-4 flex justify-end">
                        <button 
                        disabled = {isEditing || !filledRequiredFields()}
                        type = "button"
                        onClick={handleSubmitEmail}
                        className={`w-[174px] h-[56px] bg-[#F7E21E] text-base ${isEditing || !filledRequiredFields() ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer hover:text-white'} font-bold py-2 px-4 rounded-full`}>
                            Submit
                        </button>
                    </div>
                </form>
            </div>
            {showReassignConfirmationModal && (
                <div className="fixed inset-0 z-[60] bg-black bg-opacity-50 flex items-center justify-center text-center">
                    <div className="bg-white p-6 rounded-lg w-1/4">
                        {/* Center the icon using Flexbox */}
                        <div className="flex justify-center mb-2 pt-6">
                            <img src="/icons/edit.svg" className='size-11' />
                        </div>
        
                        <h1 className="text-2xl font-bold mb-3">Save & Update Details</h1>
                        <span>Lead details will be updated. Please ensure new details are correct.</span>
                        <div className="mt-6 mb-6">
                            <button
                                className="text-black underline hover:text-blue-800 focus:outline-none"
                                onClick={handleConfirmationCancel}
                            >
                                <b>No, Don't Update</b>
                            </button>
                        </div>
                        <button
                            className="bg-yellow-300 text-black py-4 w-full rounded-3xl hover:bg-yellow-400"
                            onClick={handleReassignConfirmation}
                        >
                            <b>Save & Update</b>
                        </button>
                    </div>
                </div>
            )}
        </div>
        {showSendEmailConfirmationModal && (
            fromOpp ? (
                <ConfirmEmailReKYC  leadID={leadID} handleSendEmailConfirmationCancel={handleSendEmailConfirmationCancel} handleSendEmail={handleSendEmail} />
            ) : (
                <ConfirmEmail  leadID={leadID} handleSendEmailConfirmationCancel={handleSendEmailConfirmationCancel} handleSendEmail={handleSendEmail} />
            )
        )}
        </>
    )
}

export default AdditionalDetails;